export type SolarInverterTopology = 'STRING' | 'HYBRID' | 'MICRO';

export type SolarInverterSpec = {
  acOutputKw: number;
  hybrid?: boolean | null;
  topology?: SolarInverterTopology | null;
  mpptCount?: number | null;
  modulesPerUnit?: number | null;
  /** Conservative module pairing screen, not an array DC rating. */
  minModulePowerW?: number | null;
  maxModulePowerW?: number | null;
  maxInputVoltageV?: number | null;
  maxModuleIscA?: number | null;
  /** Only set by the explicit, manufacturer-verified battery PV capability adapter. */
  integratedPvConversion?: boolean;
  maxPvInputKw?: number | null;
};

export type SolarInverterSizing = {
  panelsCount: number;
  panelCapacityWatts: number;
  includeBattery?: boolean;
  faceCount?: number | null;
  moduleVocV?: number | null;
  moduleIscA?: number | null;
  installedQuantity?: number;
};

/**
 * Advisory DC/AC window shared by the Equipment recommendation screen and the
 * Electrical fleet sizer, which aims for `target`.
 */
export const SOLAR_DC_AC_RATIO_WINDOW = {
  min: 0.8,
  target: 1.15,
  max: 1.3,
} as const;

export const INVERTER_MAX_DC_AC_RATIO = SOLAR_DC_AC_RATIO_WINDOW.max;

/** Recommendation screens are advisory; only verified limits or missing quantity data block selection. */
export const evaluateSolarInverter = (
  spec: SolarInverterSpec,
  sizing: SolarInverterSizing
): {
  quantity: number;
  totalAcKw: number | null;
  exclusionReason: string | null;
  blockingReason: string | null;
  warnings: string[];
} => {
  const micro = spec.topology === 'MICRO';
  let quantity = 1;
  if (spec.integratedPvConversion) quantity = sizing.installedQuantity ?? 1;
  else if (micro && spec.modulesPerUnit && spec.modulesPerUnit > 0)
    quantity = Math.ceil(sizing.panelsCount / spec.modulesPerUnit);
  const totalAcKw =
    spec.integratedPvConversion && quantity > 1
      ? null
      : Number((spec.acOutputKw * quantity).toFixed(6));
  const warnings: string[] = [];
  let exclusionReason: string | null = null;
  let blockingReason: string | null = null;
  if (
    !Number.isSafeInteger(sizing.panelsCount) ||
    sizing.panelsCount < 1 ||
    !Number.isFinite(sizing.panelCapacityWatts) ||
    sizing.panelCapacityWatts <= 0
  ) {
    blockingReason =
      'Add a positive whole number of modules with a known rated power.';
  } else if (micro) {
    if (
      !Number.isSafeInteger(spec.modulesPerUnit) ||
      !spec.modulesPerUnit ||
      spec.modulesPerUnit < 1
    ) {
      blockingReason =
        'Microinverter modules per unit are not verified in the catalogue.';
    } else if (spec.minModulePowerW == null || spec.maxModulePowerW == null) {
      exclusionReason =
        'Microinverter per-module power pairings are not verified in the catalogue.';
    } else if (
      sizing.panelCapacityWatts < spec.minModulePowerW ||
      sizing.panelCapacityWatts > spec.maxModulePowerW
    ) {
      exclusionReason = `Each ${sizing.panelCapacityWatts} W module is outside this microinverter’s ${spec.minModulePowerW}–${spec.maxModulePowerW} W per-module pairing screen.`;
    }
    // Pairing ranges are not absolute input limits. Always check verified
    // electrical limits independently, even when a recommendation screen fails.
    if (
      spec.maxInputVoltageV != null &&
      sizing.moduleVocV != null &&
      sizing.moduleVocV > spec.maxInputVoltageV
    ) {
      blockingReason =
        'The module open-circuit voltage exceeds the verified microinverter input limit.';
    } else if (
      spec.maxModuleIscA != null &&
      sizing.moduleIscA != null &&
      sizing.moduleIscA > spec.maxModuleIscA
    ) {
      blockingReason =
        'The module short-circuit current exceeds the verified microinverter per-module input limit.';
    }
    warnings.push(
      'Confirm module voltage and current, temperature corrections, branch circuits and grid connection against the exact manufacturer variant. Module power pairing is a recommendation screen, not electrical approval.'
    );
    if (sizing.includeBattery)
      warnings.push(
        'Microinverters supply PV only. Confirm AC-coupled storage, gateway/controller requirements and manufacturer compatibility; an integrated battery inverter is not evidence of a PV input.'
      );
  } else {
    const arrayKwp = (sizing.panelsCount * sizing.panelCapacityWatts) / 1000;
    if (spec.integratedPvConversion) {
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        blockingReason =
          'Integrated equipment quantity must be a positive whole number.';
      } else if (spec.maxPvInputKw == null) {
        warnings.push(
          'The integrated unit’s PV input power limit is not verified. Confirm the exact variant before installation.'
        );
      } else if (arrayKwp > spec.maxPvInputKw) {
        if (quantity === 1) {
          blockingReason = `The ${arrayKwp} kWp array exceeds the verified ${spec.maxPvInputKw} kW STC PV input limit of this unit.`;
        } else {
          exclusionReason =
            'The array exceeds one unit’s verified PV input limit. Allocate modules to each unit and verify the multi-unit configuration; quantity alone does not establish combined PV input capacity.';
        }
      }
      if (quantity > 1)
        warnings.push(
          'Per-unit AC, MPPT, charge and discharge ratings are not verified combined system limits. Confirm array allocation, controllers, thermal derating and grid connection.'
        );
    } else if (
      spec.acOutputKw < arrayKwp / SOLAR_DC_AC_RATIO_WINDOW.max ||
      spec.acOutputKw > arrayKwp / SOLAR_DC_AC_RATIO_WINDOW.min
    ) {
      exclusionReason =
        spec.topology == null
          ? 'Topology is unverified, so legacy single-unit DC:AC sizing applies. This unit is outside the advisory recommendation screen. The ratio alone does not establish electrical incompatibility; verify the exact variant before using microinverter quantities.'
          : 'The inverter is outside the advisory whole-array DC:AC recommendation screen. The ratio alone does not establish electrical incompatibility.';
    }
    if (
      sizing.faceCount != null &&
      spec.mpptCount != null &&
      sizing.faceCount > spec.mpptCount
    ) {
      warnings.push(
        `${sizing.faceCount} populated roof arrays and ${spec.mpptCount} independent MPPTs: confirm the stringing design. Roof polygons do not establish electrical string count or incompatibility.`
      );
    }
    if (
      sizing.includeBattery &&
      spec.topology !== 'HYBRID' &&
      spec.hybrid !== true
    )
      warnings.push(
        'Confirm a separate AC-coupled battery inverter or choose a compatible hybrid inverter for DC-coupled storage.'
      );
    warnings.push(
      'Confirm string voltage, current and MPPT allocation on site.'
    );
  }
  if (exclusionReason) warnings.push(exclusionReason);
  return {
    quantity,
    totalAcKw,
    exclusionReason: blockingReason ?? exclusionReason,
    blockingReason,
    warnings,
  };
};
